import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormLocked } from 'components/ui/FormLock';
import { setSclaSaveStatus } from './useSclaSaveStatus';

interface IUseAutosaveArgs {
  serialized: string;
  enabled: boolean;
  save: () => Promise<boolean>;
}

/**
 * True after this step's form matches the application currently on screen.
 * Changing application resets the form and holds saves until that render lands.
 */
export function useSectionReady(sectionId: string, reset: () => void): boolean {
  const [seen, setSeen] = useState(sectionId);
  if (seen !== sectionId) {
    setSeen(sectionId);
    reset();
  }
  return seen === sectionId;
}

export default function useAutosave({
  serialized,
  enabled,
  save,
}: IUseAutosaveArgs) {
  const locked = useFormLocked();
  const canSave = enabled && !locked;
  const saveRef = useRef(save);
  saveRef.current = save;
  const serializedRef = useRef(serialized);
  serializedRef.current = serialized;
  const enabledRef = useRef(canSave);
  enabledRef.current = canSave;
  const baseline = useRef<string | null>(null);
  const inFlight = useRef(false);
  const queued = useRef(false);
  const retried = useRef(false);

  const run = useCallback(async () => {
    if (!enabledRef.current || baseline.current === null) return false;
    if (serializedRef.current === baseline.current) return true;
    if (inFlight.current) {
      queued.current = true;
      return false;
    }
    inFlight.current = true;
    const snapshot = serializedRef.current;
    setSclaSaveStatus('saving');
    try {
      const ok = await saveRef.current();
      if (ok) {
        if (serializedRef.current === snapshot) baseline.current = snapshot;
        setSclaSaveStatus('saved');
        retried.current = false;
      } else {
        setSclaSaveStatus('idle');
      }
      return ok;
    } catch (error) {
      setSclaSaveStatus('error');
      if (!retried.current) {
        retried.current = true;
        window.setTimeout(() => {
          run();
        }, 2000);
      }
      throw error;
    } finally {
      inFlight.current = false;
      if (queued.current) {
        queued.current = false;
        if (serializedRef.current !== baseline.current) run();
      }
    }
  }, []);

  useEffect(() => {
    if (!canSave) {
      baseline.current = null;
      return undefined;
    }
    if (baseline.current === null) {
      baseline.current = serialized;
      return undefined;
    }
    if (serialized === baseline.current) return undefined;
    setSclaSaveStatus('saving');
    const id = window.setTimeout(() => {
      run().catch(() => undefined);
    }, 1500);
    return () => window.clearTimeout(id);
  }, [serialized, canSave, run]);

  useEffect(() => () => {
    if (!enabledRef.current || baseline.current === null) return;
    if (serializedRef.current === baseline.current) return;
    run().catch(() => undefined);
  }, [run]);

  const flush = useCallback(async () => {
    try {
      return await run();
    } catch (error) {
      return false;
    }
  }, [run]);

  return { flush };
}
